import { useTranslation } from "react-i18next";
import { ArrowLeft, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipTrigger } from "@/components/ui/tooltip";
import { DropdownMenu, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import type { WorkspaceView } from "../facilities/drilldown/nav-state.js";
import type { WorkspaceEntry, WorkspaceGroupView } from "./workspace-pages.js";

/**
 * The workspace's left navigation (ADR-032): "Back to facilities", then the caller's groups. Rows are Buttons through the
 * nav hook (ADR-023 — all navigation goes through it), the current page carries `aria-current="page"`. Under `lg` the
 * sidebar collapses to an icon rail: labels go `sr-only` (still announced) and a right-hand Tooltip shows them on hover
 * and focus. UA resets on purpose (`list-none p-0`): Preflight is excluded, a bare `<ul>` would indent 40px in dark.
 */
export function WorkspaceSidebar({
  groups,
  current,
  onSelect,
  onBack,
}: {
  groups: WorkspaceGroupView[];
  current: WorkspaceView;
  onSelect: (view: WorkspaceView) => void;
  onBack: () => void;
}) {
  const { t } = useTranslation();
  return (
    <nav aria-label={t("dashboard.dashboardAndSettings")} className="border-border bg-muted/40 flex w-14 shrink-0 flex-col gap-4 border-r py-3 max-md:hidden lg:w-64">
      <div className="px-2">
        <SidebarRow icon={ArrowLeft} label={t("workspace.backToFacilities")} onPress={onBack} />
      </div>
      {groups.map((g) => (
        <div key={g.group} className="flex flex-col gap-1 px-2">
          <h2 id={`ws-group-${g.group}`} className="text-muted-foreground sr-only px-2 text-xs font-semibold tracking-wide uppercase lg:not-sr-only">
            {t(g.headingKey)}
          </h2>
          <ul aria-labelledby={`ws-group-${g.group}`} className="m-0 flex list-none flex-col gap-0.5 p-0">
            {g.entries.map((e) => (
              <li key={e.id}>
                <EntryRow entry={e} isCurrent={e.id === current} onPress={() => onSelect(e.id)} />
              </li>
            ))}
          </ul>
        </div>
      ))}
    </nav>
  );
}

function EntryRow({ entry, isCurrent, onPress }: { entry: WorkspaceEntry; isCurrent: boolean; onPress: () => void }) {
  const { t } = useTranslation();
  return <SidebarRow icon={entry.icon} label={t(entry.labelKey)} soon={entry.status === "soon"} isCurrent={isCurrent} onPress={onPress} />;
}

function SidebarRow({
  icon: Icon,
  label,
  soon = false,
  isCurrent = false,
  onPress,
}: {
  icon: WorkspaceEntry["icon"];
  label: string;
  soon?: boolean;
  isCurrent?: boolean;
  onPress: () => void;
}) {
  return (
    <TooltipTrigger delay={0}>
      <Button
        variant={isCurrent ? "default" : "ghost"}
        aria-current={isCurrent ? "page" : undefined}
        onPress={onPress}
        className="w-full justify-start gap-2 border-0 px-2 lg:px-3"
      >
        <Icon aria-hidden="true" className="size-4 shrink-0" />
        <span className="sr-only truncate lg:not-sr-only">{label}</span>
        {soon ? <span className="sr-only ml-auto text-[10px] font-medium tracking-wide uppercase opacity-70 lg:not-sr-only">Soon</span> : null}
      </Button>
      {/* Only the collapsed rail needs it — on the wide sidebar the label is visible. */}
      <Tooltip placement="right" className="lg:hidden">
        {label}
      </Tooltip>
    </TooltipTrigger>
  );
}

/**
 * The same navigation on a phone, as ONE labelled menu instead of the icon rail (design pass 2026-11-04): twelve bare icons
 * took a fifth of a 390 px screen and said nothing until tapped. The trigger names the page you are on.
 */
export function WorkspaceMobileNav({
  groups,
  current,
  onSelect,
  onBack,
}: {
  groups: WorkspaceGroupView[];
  current: WorkspaceView;
  onSelect: (view: WorkspaceView) => void;
  onBack: () => void;
}) {
  const { t } = useTranslation();
  const currentEntry = groups.flatMap((g) => g.entries).find((e) => e.id === current);
  const CurrentIcon = currentEntry?.icon;
  return (
    <div className="border-border/60 bg-background flex items-center gap-2 border-b px-3 py-2 md:hidden">
      <Button variant="outline" size="sm" onPress={onBack} className="dark:border-border shrink-0">
        <ArrowLeft aria-hidden="true" />
        {t("facilitySelection.nav.back")}
      </Button>
      <DropdownMenuTrigger>
        <Button variant="outline" size="sm" aria-label={t("dashboard.dashboardAndSettings")} className="dark:border-border min-w-0 flex-1 justify-between gap-2">
          <span className="flex min-w-0 items-center gap-2">
            {CurrentIcon ? <CurrentIcon aria-hidden="true" className="size-4 shrink-0" /> : null}
            <span className="truncate">{currentEntry ? t(currentEntry.labelKey) : null}</span>
          </span>
          <ChevronDown aria-hidden="true" className="size-4 shrink-0" />
        </Button>
        <DropdownMenu className="min-w-60" onAction={(key) => onSelect(key as WorkspaceView)}>
          {groups.map((g) => (
            <DropdownMenuGroup key={g.group}>
              <DropdownMenuLabel>{t(g.headingKey)}</DropdownMenuLabel>
              {g.entries.map((e) => (
                <DropdownMenuItem key={e.id} id={e.id} textValue={t(e.labelKey)}>
                  <e.icon aria-hidden="true" className="size-4" />
                  {t(e.labelKey)}
                </DropdownMenuItem>
              ))}
            </DropdownMenuGroup>
          ))}
        </DropdownMenu>
      </DropdownMenuTrigger>
    </div>
  );
}
